import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API } from '../config/api';
import {
  Link2, Unlink, Plus, Check, Loader2, X, ChevronLeft,
  History, Globe
} from 'lucide-react';

const PLATFORM_ICONS = {
  discord: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
    </svg>
  ),
  twitter: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
  ),
  facebook: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  ),
  instagram: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
    </svg>
  ),
  youtube: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z"/>
      <path d="M9.545 15.568V8.432L15.818 12l-6.273 3.568z" fill="white"/>
    </svg>
  ),
  tiktok: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
    </svg>
  ),
  linkedin: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
  )
};

const PLATFORM_COLORS = {
  discord: 'bg-[#5865F2]', twitter: 'bg-black', facebook: 'bg-[#1877F2]',
  instagram: 'bg-gradient-to-r from-[#833AB4] via-[#E4405F] to-[#FCAF45]',
  youtube: 'bg-[#FF0000]', tiktok: 'bg-black', linkedin: 'bg-[#0A66C2]'
};

export { PLATFORM_ICONS, PLATFORM_COLORS };

export default function SocialSyncPage() {
  const { isAuthenticated, loading: authLoading, getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [platforms, setPlatforms] = useState({});
  const [connections, setConnections] = useState([]);
  const [history, setHistory] = useState([]);
  const [oauthConfig, setOauthConfig] = useState({});
  const [oauthAccounts, setOauthAccounts] = useState([]);
  const [activeTab, setActiveTab] = useState('connections');
  const [showConnect, setShowConnect] = useState(null);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [connecting, setConnecting] = useState(false);

  // Handle OAuth redirect callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthPlatform = params.get('oauth');
    const status = params.get('status');
    const account = params.get('account');
    const message = params.get('message');

    if (oauthPlatform && status) {
      if (status === 'success') {
        toast.success(`${oauthPlatform} connected${account ? ` as ${decodeURIComponent(account)}` : ''}!`);
      } else {
        toast.error(`${oauthPlatform} connection failed: ${message || 'Unknown error'}`);
      }
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/login'); return; }
    fetchData();
  }, [isAuthenticated, authLoading]);

  const fetchData = async () => {
    const headers = getAuthHeaders();
    try {
      const [platRes, connRes, histRes, oauthCfgRes, oauthAccRes] = await Promise.all([
        fetch(`${API}/social/platforms`).catch(() => null),
        fetch(`${API}/social/connections`, { headers }).catch(() => null),
        fetch(`${API}/social/cross-posts?limit=20`, { headers }).catch(() => null),
        fetch(`${API}/social-oauth/config-status`).catch(() => null),
        fetch(`${API}/social-oauth/accounts`, { headers }).catch(() => null),
      ]);
      if (platRes?.ok) { const d = await platRes.json(); setPlatforms(d.platforms || {}); }
      if (connRes?.ok) { const d = await connRes.json(); setConnections(d.connections || []); }
      if (histRes?.ok) { const d = await histRes.json(); setHistory(d.cross_posts || []); }
      if (oauthCfgRes?.ok) { const d = await oauthCfgRes.json(); setOauthConfig(d.platforms || {}); }
      if (oauthAccRes?.ok) { const d = await oauthAccRes.json(); setOauthAccounts(d.accounts || []); }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  // OAuth connect — redirect to platform authorization
  const connectOAuth = async (platform) => {
    setConnecting(true);
    try {
      const res = await fetch(`${API}/social-oauth/authorize/${platform}`, {
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (res.ok && data.authorize_url) {
        window.location.href = data.authorize_url;
        return;
      }
      toast.error(data.detail || 'Failed to start OAuth flow');
    } catch {
      toast.error('Connection failed');
    } finally {
      setConnecting(false);
    }
  };

  // OAuth disconnect
  const disconnectOAuth = async (platform) => {
    if (!window.confirm(`Disconnect ${platform}?`)) return;
    try {
      const res = await fetch(`${API}/social-oauth/disconnect/${platform}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        toast.success(`${platform} disconnected`);
        setOauthAccounts(prev => prev.filter(a => a.platform !== platform));
        fetchData();
      }
    } catch {
      toast.error('Failed to disconnect');
    }
  };

  const connectPlatform = async (platform) => {
    if (platform === 'discord' && !webhookUrl.trim()) { toast.error('Enter a Discord webhook URL'); return; }
    setConnecting(true);
    try {
      const body = { platform };
      if (platform === 'discord') body.webhook_url = webhookUrl;
      const res = await fetch(`${API}/social/connect`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (res.ok) { toast.success(data.message || 'Connected!'); setShowConnect(null); setWebhookUrl(''); fetchData(); }
      else { toast.error(data.detail || 'Connection failed'); }
    } catch { toast.error('Connection failed'); }
    finally { setConnecting(false); }
  };

  const disconnectPlatform = async (connectionId) => {
    if (!confirm('Disconnect this account?')) return;
    try {
      const res = await fetch(`${API}/social/connections/${connectionId}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (res.ok) { setConnections(prev => prev.filter(c => c.connection_id !== connectionId)); toast.success('Disconnected'); }
    } catch { toast.error('Failed to disconnect'); }
  };

  const toggleAutoPost = async (connectionId, value) => {
    try {
      await fetch(`${API}/social/connections/${connectionId}/defaults`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ auto_post: value })
      });
      setConnections(prev => prev.map(c => c.connection_id === connectionId ? { ...c, auto_post: value } : c));
    } catch {}
  };

  if (authLoading || loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-400" /></div>;
  }

  const connectedPlatforms = new Set(connections.map(c => c.platform));
  const availablePlatforms = Object.entries(platforms).filter(([k]) => !connectedPlatforms.has(k));

  return (
    <div className="min-h-screen pb-32" data-testid="social-sync-page">
      <div className="border-b border-border bg-background/50">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}><ChevronLeft className="w-5 h-5" /></Button>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <Globe className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Social Sync</h1>
              <p className="text-sm text-muted-foreground">Post once, publish everywhere</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 mt-6">
        <div className="flex gap-1 mb-6">
          {[{ key: 'connections', label: 'Connections', icon: Link2 }, { key: 'history', label: 'History', icon: History }].map(tab => (
            <Button key={tab.key} variant={activeTab === tab.key ? 'default' : 'ghost'}
              className={activeTab === tab.key ? 'bg-blue-600 hover:bg-blue-700' : 'text-muted-foreground'}
              onClick={() => setActiveTab(tab.key)} size="sm">
              <tab.icon className="w-3.5 h-3.5 mr-1.5" />{tab.label}
            </Button>
          ))}
        </div>

        {activeTab === 'connections' && (
          <div className="space-y-6">
            {/* OAuth Connected Accounts */}
            {oauthAccounts.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">OAuth Connected Accounts</h2>
                <div className="space-y-3">
                  {oauthAccounts.map(acc => (
                    <Card key={acc.platform} className="bg-muted/20 border-border p-4" data-testid={`oauth-connection-${acc.platform}`}>
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg ${PLATFORM_COLORS[acc.platform] || 'bg-muted'} flex items-center justify-center text-white shrink-0`}>
                          {PLATFORM_ICONS[acc.platform]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm text-white">{oauthConfig[acc.platform]?.name || acc.platform}</p>
                            <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                              <Check className="w-3 h-3 mr-1" />Connected
                            </Badge>
                          </div>
                          {acc.account_name && <p className="text-xs text-muted-foreground">{acc.account_name}</p>}
                        </div>
                        <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300"
                          onClick={() => disconnectOAuth(acc.platform)} data-testid={`disconnect-oauth-${acc.platform}`}>
                          <Unlink className="w-4 h-4 mr-1" /> Disconnect
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Existing webhook-based connections */}
            {connections.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Webhook Connections</h2>
                <div className="space-y-3">
                  {connections.map(conn => (
                    <Card key={conn.connection_id} className="bg-muted/20 border-border p-4" data-testid={`connection-${conn.platform}`}>
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg ${PLATFORM_COLORS[conn.platform] || 'bg-muted'} flex items-center justify-center text-white`}>
                          {PLATFORM_ICONS[conn.platform]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm">{platforms[conn.platform]?.name || conn.platform}</p>
                            <Badge className={conn.status === 'connected' ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-blue-500/20 text-blue-400 border-blue-500/30'}>
                              {conn.status === 'connected' ? 'Connected' : 'Smart Share'}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">{conn.account_name}</p>
                        </div>
                        {conn.status === 'connected' && (
                          <div className="flex items-center gap-2">
                            <Label className="text-xs text-muted-foreground">Auto</Label>
                            <Switch checked={conn.auto_post} onCheckedChange={(val) => toggleAutoPost(conn.connection_id, val)} data-testid={`auto-post-toggle-${conn.platform}`} />
                          </div>
                        )}
                        <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-300"
                          onClick={() => disconnectPlatform(conn.connection_id)} data-testid={`disconnect-${conn.platform}`}>
                          <Unlink className="w-4 h-4" />
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* OAuth Connect Buttons */}
            <div>
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                Connect Your Platforms
              </h2>
              <p className="text-xs text-muted-foreground mb-4">Sign in with your social media accounts to publish content directly from DMV Soundstage.</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {['instagram', 'youtube', 'twitter', 'tiktok'].map(platform => {
                  const cfg = oauthConfig[platform];
                  const isConnected = oauthAccounts.some(a => a.platform === platform);
                  const isConfigured = cfg?.configured;

                  return (
                    <Card key={platform}
                      className={`border p-4 transition-all ${
                        isConnected
                          ? 'bg-green-500/5 border-green-500/20'
                          : isConfigured
                          ? 'bg-muted/20 border-border hover:border-blue-500/30 cursor-pointer'
                          : 'bg-white/[0.01] border-border opacity-70'
                      }`}
                      data-testid={`oauth-platform-${platform}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg ${PLATFORM_COLORS[platform] || 'bg-muted'} flex items-center justify-center text-white shrink-0`}>
                          {PLATFORM_ICONS[platform]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-white">{cfg?.name || platform}</p>
                          {isConnected ? (
                            <p className="text-xs text-green-400">Connected</p>
                          ) : isConfigured ? (
                            <p className="text-xs text-muted-foreground">Click to connect via OAuth</p>
                          ) : (
                            <p className="text-xs text-muted-foreground/80">Awaiting configuration</p>
                          )}
                        </div>
                        {isConnected ? (
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/30"><Check className="w-3 h-3 mr-1" />Active</Badge>
                        ) : (
                          <Button
                            size="sm"
                            disabled={!isConfigured || connecting}
                            onClick={() => connectOAuth(platform)}
                            className={isConfigured
                              ? 'bg-blue-600 hover:bg-blue-700 text-foreground text-xs'
                              : 'bg-muted text-muted-foreground text-xs cursor-not-allowed'
                            }
                            data-testid={`connect-oauth-${platform}`}
                          >
                            {connecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5 mr-1" />}
                            Connect
                          </Button>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>

              {!Object.values(oauthConfig).some(c => c?.configured) && (
                <Card className="mt-4 bg-amber-500/5 border-amber-500/20 p-4">
                  <p className="text-sm text-amber-400 font-medium">OAuth credentials not configured yet</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    To enable direct platform connections, environment variables need to be set:
                    META_APP_ID, META_APP_SECRET, GOOGLE_CLIENT_ID, GOOGLE_CLIENT_SECRET, TWITTER_CLIENT_ID, TWITTER_CLIENT_SECRET, TIKTOK_CLIENT_KEY, TIKTOK_CLIENT_SECRET
                  </p>
                </Card>
              )}
            </div>

            {/* Legacy: Add webhook platforms */}
            {availablePlatforms.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                  Webhook / Smart Share
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {availablePlatforms.map(([key, plat]) => (
                    <Card key={key} className="bg-muted/20 border-border p-4 hover:border-blue-500/20 transition-all cursor-pointer"
                      onClick={() => setShowConnect(key)} data-testid={`add-platform-${key}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg ${PLATFORM_COLORS[key] || 'bg-muted'} flex items-center justify-center text-white`}>
                          {PLATFORM_ICONS[key]}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-sm">{plat.name}</p>
                          <p className="text-xs text-muted-foreground">{plat.description}</p>
                        </div>
                        <Plus className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'history' && (
          <div>
            {history.length === 0 ? (
              <Card className="bg-muted/20 border-border p-8 text-center">
                <History className="w-10 h-10 text-muted-foreground/80 mx-auto mb-3" />
                <p className="text-muted-foreground">No cross-posts yet</p>
                <p className="text-xs text-muted-foreground mt-1">When you cross-post, results will appear here</p>
              </Card>
            ) : (
              <div className="space-y-2" data-testid="cross-post-history">
                {history.map(cp => (
                  <Card key={cp.cross_post_id} className="bg-muted/20 border-border p-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg ${PLATFORM_COLORS[cp.platform] || 'bg-muted'} flex items-center justify-center text-white`}>
                        {PLATFORM_ICONS[cp.platform]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{platforms[cp.platform]?.name || cp.platform}</p>
                        <p className="text-xs text-muted-foreground truncate">{cp.caption?.substring(0, 80)}</p>
                      </div>
                      <Badge className={
                        cp.status === 'posted' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                        cp.status === 'smart_share' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                        cp.status === 'failed' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                        'bg-muted/40 text-muted-foreground border-border/30'
                      }>
                        {cp.status === 'posted' ? 'Posted' : cp.status === 'smart_share' ? 'Shared' : cp.status === 'failed' ? 'Failed' : cp.status}
                      </Badge>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Connect Modal */}
      {showConnect && (
        <div className="fixed inset-0 bg-background/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowConnect(null)}>
          <Card className="w-full max-w-md bg-card border-border p-5" onClick={e => e.stopPropagation()} data-testid="connect-modal">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${PLATFORM_COLORS[showConnect]} flex items-center justify-center text-white`}>
                  {PLATFORM_ICONS[showConnect]}
                </div>
                <h3 className="font-bold text-lg">Connect {platforms[showConnect]?.name}</h3>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setShowConnect(null)}><X className="w-4 h-4" /></Button>
            </div>
            {showConnect === 'discord' ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">Paste your Discord webhook URL. Posts will auto-publish to this channel.</p>
                <div>
                  <Label className="text-sm text-foreground/80 mb-1 block">Webhook URL</Label>
                  <Input data-testid="discord-webhook-input" value={webhookUrl} onChange={e => setWebhookUrl(e.target.value)}
                    placeholder="https://discord.com/api/webhooks/..." className="bg-muted/50 border-border" />
                  <p className="text-[10px] text-muted-foreground/80 mt-1">Server Settings → Integrations → Webhooks → Copy URL</p>
                </div>
                <Button className="w-full bg-[#5865F2] hover:bg-[#4752C4]" onClick={() => connectPlatform('discord')}
                  disabled={connecting || !webhookUrl.trim()} data-testid="connect-discord-btn">
                  {connecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}Connect Discord
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Connect {platforms[showConnect]?.name} in <strong>Smart Share</strong> mode. We'll prepare your content with a pre-filled caption and media — ready for one-tap sharing.
                </p>
                <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <p className="text-xs text-blue-400">Auto-posting will activate when API integration is configured. Smart Share gives you a streamlined sharing experience now.</p>
                </div>
                <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={() => connectPlatform(showConnect)}
                  disabled={connecting} data-testid={`connect-${showConnect}-btn`}>
                  {connecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}Enable Smart Share
                </Button>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
